class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      const hasDesktopFilters = document.getElementById('FacetFiltersForm') !== null;
      const includeFiltersFormId = hasDesktopFilters ? 'FacetFiltersForm' : 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetSortDrawerForm' ||
            form.id === includeFiltersFormId
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/* ==========================================================================
   Design-audit additions (2026-09-05, "grid" lane)
   grid-1, grid-4, tasks-5, comp_global-4, comp_nzau-4.
   ========================================================================== */

/**
 * Quick filter chips above the collection grid: In stock / Enchanted / Foil.
 * Each chip toggles one or more `filter.*` query params and re-renders the
 * grid through the SAME AJAX path the rest of this file already uses
 * (FacetFiltersForm.renderPage), so a chip click behaves exactly like
 * checking a facet checkbox -- one network round trip, URL kept in sync via
 * renderPage's own history.pushState, no full page reload. Chips default OFF
 * (see facets.liquid) so this never recreates the sessionStorage redirect
 * that perf-2/grid-2 remove elsewhere.
 */
class QuickFilterChips extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('.quick-filter-chip').forEach((button) => {
      button.addEventListener('click', this.onChipClick.bind(this));
    });
  }

  onChipClick(event) {
    const button = event.currentTarget;
    const wasActive = button.getAttribute('aria-pressed') === 'true';
    const param = button.dataset.qcParam;
    const mode = button.dataset.qcMode;
    if (!param) return;

    const params = new URLSearchParams(window.location.search);
    // A filter change always restarts at page 1 (Dawn's own facet path drops
    // `page` too); keeping page=3 on a narrower result set would render empty.
    params.delete('page');

    if (mode === 'multi') {
      const values = (button.dataset.qcValues || '').split('||').filter(Boolean);
      const kept = params.getAll(param).filter((value) => values.indexOf(value) === -1);
      params.delete(param);
      kept.forEach((value) => params.append(param, value));
      if (!wasActive) values.forEach((value) => params.append(param, value));
    } else {
      const value = button.dataset.qcValue;
      const kept = params.getAll(param).filter((existing) => existing !== value);
      params.delete(param);
      kept.forEach((existing) => params.append(param, existing));
      if (!wasActive) params.append(param, value);
    }

    // grid-C (2026-09-05 evening): Normal / Foil / Enchanted also gate on
    // "in stock" -- a shopper tapping "Normal" wants normals they can
    // actually buy, not a phantom sold-out-only result set. Applied and
    // removed together with the chip's own filter, EXCEPT removal leaves
    // the availability param alone if a separately-toggled "In stock" chip
    // is still active -- turning off "Normal" should never silently turn
    // off "In stock" out from under the shopper.
    if (button.dataset.qcAvailability) {
      const AVAILABILITY_PARAM = 'filter.v.availability';
      if (!wasActive) {
        if (params.getAll(AVAILABILITY_PARAM).indexOf('1') === -1) {
          params.append(AVAILABILITY_PARAM, '1');
        }
      } else {
        let otherAvailabilityActive = false;
        this.querySelectorAll('.quick-filter-chip[data-qc-param="' + AVAILABILITY_PARAM + '"]').forEach(
          (sibling) => {
            if (sibling !== button && sibling.getAttribute('aria-pressed') === 'true') {
              otherAvailabilityActive = true;
            }
          }
        );
        if (!otherAvailabilityActive) params.delete(AVAILABILITY_PARAM);
      }
    }

    button.setAttribute('aria-pressed', wasActive ? 'false' : 'true');
    button.classList.toggle('is-active', !wasActive);

    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(params.toString());
    } else {
      window.location.search = params.toString();
    }
  }

  /**
   * Re-derive every chip's pressed state from a query string. Called after
   * ANY facet render (checkbox, active-filter "x" pill, sort, Back button), so
   * a chip never stays gold after its filter was cleared through another
   * control. The chips live outside the elements facets.js re-renders, so
   * they would otherwise only ever change on their own click.
   */
  syncFromParams(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    this.querySelectorAll('.quick-filter-chip').forEach((button) => {
      const param = button.dataset.qcParam;
      if (!param) return;
      const current = params.getAll(param);
      let active;
      if (button.dataset.qcMode === 'multi') {
        const values = (button.dataset.qcValues || '').split('||').filter(Boolean);
        active = values.some((value) => current.indexOf(value) !== -1);
      } else {
        active = current.indexOf(button.dataset.qcValue) !== -1;
      }
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.classList.toggle('is-active', active);
    });
  }
}

if (!customElements.get('quick-filter-chips')) {
  customElements.define('quick-filter-chips', QuickFilterChips);
}

// Keep chip state in step with every render path (checkbox change, facet
// remove, popstate): wrap the static renderPage rather than duplicating its
// callers. All of Dawn's internal calls go through `FacetFiltersForm.renderPage`,
// so the wrapper sees them; `updateURLHash` keeps its default when undefined.
if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
  const originalRenderPage = FacetFiltersForm.renderPage;
  FacetFiltersForm.renderPage = function (searchParams, event, updateURLHash) {
    const result =
      updateURLHash === undefined
        ? originalRenderPage.call(FacetFiltersForm, searchParams, event)
        : originalRenderPage.call(FacetFiltersForm, searchParams, event, updateURLHash);
    document.querySelectorAll('quick-filter-chips').forEach((chips) => {
      if (typeof chips.syncFromParams === 'function') chips.syncFromParams(searchParams);
    });
    return result;
  };
}

/**
 * Tappable printing pills (grid-4 / comp_global-4) + the quick-shop stepper
 * (request A, 2026-09-05 evening -- replaces the old x4 playset button,
 * which always added 4 even when fewer were in stock). Delegated on
 * `document` because both are re-rendered by facets.js's own AJAX swap of
 * #product-grid on every filter/sort/chip change -- a per-element listener
 * would be destroyed on the very first interaction, a delegated one on
 * document survives it for free.
 *
 * Mirrors Dawn's own product-form.js add-to-cart flow (see fetchConfig,
 * cart.getSectionsToRender / cart.renderContents, PUB_SUB_EVENTS.cartUpdate)
 * so the cart drawer refreshes exactly the way every other add-to-cart
 * control on this theme already does.
 */
(function () {
  function findCartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  /**
   * 2026-09-05 (operator): quick adds no longer open the cart drawer. The drawer
   * and the header bubble are refreshed quietly and a small bottom-centre toast
   * confirms the add with a "View cart" action that opens the drawer on demand.
   */
  function quietRenderCart(cart, response) {
    if (!cart || !response || !response.sections) return false;
    try {
      cart.classList.remove('is-empty');
      var inner = cart.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      var sections = typeof cart.getSectionsToRender === 'function' ? cart.getSectionsToRender() : [];
      for (var i = 0; i < sections.length; i++) {
        var section = sections[i];
        var el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        var html = response.sections[section.id];
        if (!el || !html) continue;
        el.innerHTML = typeof cart.getSectionInnerHTML === 'function' ? cart.getSectionInnerHTML(html, section.selector) : html;
      }
      var overlay = cart.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof cart.close === 'function') overlay.addEventListener('click', cart.close.bind(cart));
      return true;
    } catch (e) {
      return false;
    }
  }

  function showPillToast(button, message, isError) {
    var toast = document.getElementById('inkwell-cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'inkwell-cart-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    var hide = function () { toast.classList.remove('is-visible'); };
    toast.className = 'ink-toast' + (isError ? ' ink-toast--error' : '');
    toast.innerHTML = '';
    var msg = document.createElement('span');
    msg.className = 'ink-toast__msg';
    msg.textContent = message;
    toast.appendChild(msg);
    if (!isError) {
      var action = document.createElement('button');
      action.type = 'button';
      action.className = 'ink-toast__action';
      action.textContent = 'View cart';
      action.addEventListener('click', function () {
        var c = findCartElement();
        hide();
        if (c && typeof c.open === 'function') c.open();
      });
      toast.appendChild(action);
    }
    // force a reflow so the transition replays on rapid successive adds
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    window.clearTimeout(toast._inkTimer);
    toast._inkTimer = window.setTimeout(hide, isError ? 3400 : 2800);
  }

  function onQuickAddClick(event) {
    const button = event.target.closest ? event.target.closest('.js-pp-quick-add') : null;
    if (!button || button.disabled) return;
    event.preventDefault();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const cart = findCartElement();
    button.disabled = true;
    button.classList.add('loading');

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', button.dataset.qty || '1');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      body.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) {
          showPillToast(button, response.description || response.message || 'Could not add to cart', true);
          return;
        }

        if (cart && quietRenderCart(cart, response)) {
          // drawer + header bubble refreshed quietly; the toast below confirms the add
        } else if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        } else if (window.routes && window.routes.cart_url) {
          window.location = window.routes.cart_url;
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'quick-filter-chips',
            productVariantId: variantId,
            cartData: response,
          });
        }

        showPillToast(button, 'Added to cart');
      })
      .catch(function () {
        showPillToast(button, 'Could not add to cart', true);
      })
      .finally(function () {
        // Fix round 2 (grid-major): mirrors Dawn's own product-form.js
        // .finally() block exactly (see onSubmitHandler in product-form.js).
        // cart-drawer.js's renderContents() swaps #CartDrawer's *innerHTML*
        // from the "has items" section markup but never touches the
        // is-empty class living on the outer <cart-drawer> element itself
        // (see snippets/cart-drawer.liquid: `<cart-drawer class="drawer{%
        // if cart == empty %} is-empty{% endif %}">`). renderContents()
        // schedules `this.open()` in its own setTimeout, and open() branches
        // on `this.classList.contains('is-empty')` to decide which container
        // to hand to trapFocus(): when a first add-to-cart happens from a
        // genuinely empty cart, that class is still "is-empty" (stale) at
        // the moment open() runs, so it looks for `.drawer__inner-empty` --
        // which no longer exists once the non-empty markup has been swapped
        // in -- and trapFocus(null, ...) then throws inside
        // getFocusableElements' `container.querySelectorAll(...)`. Dawn's
        // product-form.js avoids this by clearing the stale class here, in
        // its .finally(), which (being a microtask) always runs before
        // renderContents()'s internal setTimeout (a macrotask) fires. This
        // handler had no equivalent line, so the exact same call sequence
        // (fetch -> renderContents -> open -> trapFocus) hit the null that
        // Dawn's own code path never reaches. Not a cart-drawer.js fix (out
        // of this lane's file list) -- this is the missing caller-side step.
        button.disabled = false;
        button.classList.remove('loading');
        if (cart && cart.classList && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      });
  }

  document.addEventListener('click', onQuickAddClick);

  /**
   * Quick shop widget (request A, 2026-09-05 evening): a quantity stepper +
   * Add button rendered on every card by card-product.liquid whenever
   * `quick_shop_enabled` is passed (collection grid, list view, and search
   * results), shown/hidden purely by the `.quick-shop-mode` class on <html>
   * (see the toggle in main-collection-product-grid.liquid and the mode-sync
   * script in main-search.liquid). Reuses findCartElement/showPillToast
   * above rather than duplicating them.
   *
   * Distribution: data-qs-variants on the widget is a JSON array of
   * {id, qty, tier} built server-side in card-product.liquid (tier 0 =
   * Normal, 1 = Cold Foil, 2 = Holofoil/Rainbow Foil, 3 = everything else).
   * Typing N and hitting Add fills tier 0 first, then 1, then 2, then 3,
   * highest-stock variant first within a tier, capping each variant at its
   * own qty -- exactly the operator's stated preference order.
   */
  function qsFindWidget(el) {
    return el && el.closest ? el.closest('[data-qs-widget]') : null;
  }

  function qsParseVariants(widget) {
    try {
      var parsed = JSON.parse(widget.dataset.qsVariants || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function qsMaxQty(widget) {
    var variants = qsParseVariants(widget);
    var max = 0;
    for (var i = 0; i < variants.length; i++) {
      max += Number(variants[i].qty) || 0;
    }
    return max;
  }

  function qsSetValue(widget, value) {
    var input = widget.querySelector('[data-qs-input]');
    if (!input) return;
    var max = qsMaxQty(widget);
    var next = Math.round(Number(value));
    if (isNaN(next)) next = 0;
    if (next < 0) next = 0;
    if (next > max) next = max;
    input.value = next;
  }

  function qsSortVariants(variants) {
    return variants.slice().sort(function (a, b) {
      var tierA = Number(a.tier) || 0;
      var tierB = Number(b.tier) || 0;
      if (tierA !== tierB) return tierA - tierB;
      return (Number(b.qty) || 0) - (Number(a.qty) || 0);
    });
  }

  function qsDistribute(variants, requestedQty) {
    var sorted = qsSortVariants(variants);
    var remaining = requestedQty;
    var items = [];
    for (var i = 0; i < sorted.length && remaining > 0; i++) {
      var variant = sorted[i];
      var qty = Number(variant.qty) || 0;
      var id = variant.id;
      if (!id || qty <= 0) continue;
      var take = Math.min(remaining, qty);
      items.push({ id: id, quantity: take });
      remaining -= take;
    }
    return { items: items, allocated: requestedQty - remaining };
  }

  function onQsStepClick(event) {
    var target = event.target;
    var button = (target.closest && (target.closest('[data-qs-minus]') || target.closest('[data-qs-plus]'))) || null;
    if (!button || button.disabled) return;
    var widget = qsFindWidget(button);
    if (!widget) return;
    var input = widget.querySelector('[data-qs-input]');
    if (!input) return;
    var current = Number(input.value) || 0;
    var delta = button.hasAttribute('data-qs-plus') ? 1 : -1;
    qsSetValue(widget, current + delta);
  }

  function onQsInputChange(event) {
    var input = event.target.closest ? event.target.closest('[data-qs-input]') : null;
    if (!input) return;
    var widget = qsFindWidget(input);
    if (!widget) return;
    qsSetValue(widget, input.value);
  }

  function onQsAddClick(event) {
    var button = event.target.closest ? event.target.closest('[data-qs-add]') : null;
    if (!button || button.disabled) return;
    event.preventDefault();

    var widget = qsFindWidget(button);
    if (!widget) return;
    var input = widget.querySelector('[data-qs-input]');
    var requested = input ? Math.round(Number(input.value)) || 0 : 0;
    if (requested <= 0) return;

    var variants = qsParseVariants(widget);
    var result = qsDistribute(variants, requested);
    if (!result.items.length) return;

    var title = widget.dataset.qsTitle || 'item';
    var cart = findCartElement();
    button.disabled = true;
    button.classList.add('loading');
    if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);

    var body = {
      items: result.items,
      sections_url: window.location.pathname,
    };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (section) {
        return section.id;
      });
    } else {
      body.sections = ['cart-drawer', 'cart-icon-bubble'];
    }

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) {
          showPillToast(button, response.description || response.message || 'Could not add to cart', true);
          return;
        }

        if (cart && quietRenderCart(cart, response)) {
          // drawer + header bubble refreshed quietly; the toast below confirms the add
        } else if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        } else if (window.routes && window.routes.cart_url) {
          window.location = window.routes.cart_url;
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'quick-shop',
            cartData: response,
          });
        }

        if (result.allocated >= requested) {
          showPillToast(button, 'Added ' + requested + ' × ' + title);
        } else {
          showPillToast(
            button,
            'Added ' + result.allocated + ' of ' + requested + ' (only ' + result.allocated + ' in stock)'
          );
        }
        qsSetValue(widget, 0);
      })
      .catch(function () {
        showPillToast(button, 'Could not add to cart', true);
      })
      .finally(function () {
        // Same is-empty fix as onQuickAddClick above -- see the long comment
        // there for why this has to run in .finally().
        button.disabled = false;
        button.classList.remove('loading');
        if (cart && cart.classList && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      });
  }

  document.addEventListener('click', onQsStepClick);
  document.addEventListener('click', onQsAddClick);
  document.addEventListener('change', onQsInputChange);

  /**
   * "Finish" facet grouping (request B, 2026-09-05 evening). The visible
   * group checkbox (rendered in facets.liquid) controls a set of REAL,
   * visually-hidden checkboxes underneath it in the same <label> -- one per
   * underlying "{Set} - {Printing}" value, all sharing
   * name="filter.v.option.version" -- so Dawn's own facets.js form
   * serialisation (FacetFiltersForm.onSubmitHandler, bound to the `input`
   * event on the enclosing <form>) keeps working unmodified. This handler's
   * only job is: set every underlying checkbox's `checked` to match the
   * group checkbox, then dispatch one bubbling `input` event so the
   * existing listener picks it up -- exactly as if a shopper had somehow
   * checked every underlying box by hand.
   */
  function onFinishGroupChange(event) {
    var groupCheckbox = event.target;
    if (!groupCheckbox.matches || !groupCheckbox.matches('[data-finish-group-toggle]')) return;
    var label = groupCheckbox.closest('label');
    if (!label) return;
    var underlying = label.querySelectorAll('.finish-group-checkbox__underlying input[type="checkbox"]');
    if (!underlying.length) return;
    var checked = groupCheckbox.checked;
    var lastInput = null;
    underlying.forEach(function (input) {
      if (input.disabled) return;
      input.checked = checked;
      lastInput = input;
    });
    if (lastInput) {
      lastInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  document.addEventListener('change', onFinishGroupChange);

  /**
   * "Finish" active-filter pill removal (request B). These pills replace
   * Dawn's normal per-value <facet-remove><a href="..."> pattern because a
   * single value's `url_to_remove` only strips THAT one value -- removing a
   * whole Finish group (which can be 1-3 underlying values) needs all of
   * them stripped from the query string together. Mirrors the URLSearchParams
   * approach QuickFilterChips already uses above rather than inventing a
   * second mechanism.
   */
  function onFinishGroupRemoveClick(event) {
    var button = event.target.closest ? event.target.closest('.js-finish-group-remove') : null;
    if (!button) return;
    event.preventDefault();

    var param = button.dataset.fgRemoveParam;
    var values = (button.dataset.fgRemoveValues || '').split('||').filter(Boolean);
    if (!param || !values.length) return;

    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    var kept = params.getAll(param).filter(function (existing) {
      return values.indexOf(existing) === -1;
    });
    params.delete(param);
    kept.forEach(function (existing) {
      params.append(param, existing);
    });

    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(params.toString());
    } else {
      window.location.search = params.toString();
    }
  }

  document.addEventListener('click', onFinishGroupRemoveClick);
})();
